import { useEffect, useRef, useState } from "react";
import useAxios from "../../../../../hooks/useAxios";
import useLoading from "../../../../../shared/components/loading/useLoading";
import { useTracking } from "react-tracking";
import {
  catchFunction,
  spanishDateTransformer,
} from "../../../../../shared/lib/utils";
import {
  DASHBOARDS_DATA_ALERTS_RECORDS,
  DASHBOARDS_DATA_ALERTS_TAG_SELECTABLES,
} from "../../../../../shared/lib/apiEndpointsResolver";
import BreadcrumbPanel from "../../../../../shared/components/breadcrumb/BreadcrumbPanel";
import { DASHBOARDS } from "../../../../../shared/lib/routesResolver";
import DataAlertsDashboardTable from "./DataAlertsDashboardTable";
import DataAlertsFilters from "../../shared/DataAlertsFilters";
import OverviewTabs from "../../../../../shared/components/overview-panel/OverviewTabs";
import SearchInput from "../../../../../shared/components/input/SearchInput";
import OverviewTabContent from "../../../../../shared/components/overview-panel/OverviewTabContent";

const DataAlertsDashboardContainer = () => {
  const axios = useAxios();
  const { setLoading } = useLoading();
  const recordsRef = useRef();
  const [records, setRecords] = useState([]);
  const [errorRecords, setErrorRecords] = useState([]);
  const [criticalRecords, setCriticalRecords] = useState([]);
  const [infoRecords, setInfoRecords] = useState([]);
  const [warningRecords, setWarningRecords] = useState([]);
  const [tagSelectables, setTagSelectables] = useState([]);
  const [params, setParams] = useState({
    dateRange: { from: new Date(), to: new Date() },
    tags: [],
  });
  const { trackEvent } = useTracking();
  const [evaluationDate, setEvaluationDate] = useState({ date: new Date() });

  const SEVERITY = {
    INFO: "INFO",
    CRITICAL: "CRITICAL",
    ERROR: "ERROR",
    WARNING: "WARNING",
  };

  const breadcrumbItems = [
    { title: "dashboards.dataAlerts.breadcrumb.first", link: DASHBOARDS },
    { title: "dashboards.dataAlerts.breadcrumb.second" },
  ];

  const findDataAlertRecords = () => {
    axios
      .post(DASHBOARDS_DATA_ALERTS_RECORDS, params)
      .then((response) => {
        recordsRef.current = response.data;
        setRecords(response.data);
        setEvaluationDate(response.data[0]?.ts);
        setErrorRecords(
          response.data.filter((x) => x.severity === SEVERITY.ERROR),
        );
        setCriticalRecords(
          response.data.filter((x) => x.severity === SEVERITY.CRITICAL),
        );
        setInfoRecords(
          response.data.filter((x) => x.severity === SEVERITY.INFO),
        );
        setWarningRecords(
          response.data.filter((x) => x.severity === SEVERITY.WARNING),
        );
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const findTagSelectables = () => {
    axios
      .post(DASHBOARDS_DATA_ALERTS_TAG_SELECTABLES, {
        dateRange: params.dateRange,
      })
      .then((response) => setTagSelectables(response.data))
      .catch(catchFunction);
  };

  const filterByMessage = (record, searchTerm, severity) => {
    return (
      record.message.toLowerCase().includes(searchTerm.toLowerCase()) &&
      record.severity === severity
    );
  };

  const filterDataAlertsByMessage = (changeEvent) => {
    const searchTerm = changeEvent.target.value;

    setRecords(
      recordsRef.current.filter((x) =>
        x.message.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
    setErrorRecords(
      recordsRef.current.filter((x) =>
        filterByMessage(x, searchTerm, SEVERITY.ERROR),
      ),
    );
    setCriticalRecords(
      recordsRef.current.filter((x) =>
        filterByMessage(x, searchTerm, SEVERITY.CRITICAL),
      ),
    );
    setInfoRecords(
      recordsRef.current.filter((x) =>
        filterByMessage(x, searchTerm, SEVERITY.INFO),
      ),
    );
    setWarningRecords(
      recordsRef.current.filter((x) =>
        filterByMessage(x, searchTerm, SEVERITY.WARNING),
      ),
    );
  };

  const SearchInputComponent = (
    <SearchInput
      onChangeHandler={filterDataAlertsByMessage}
      placeholder="dashboards.dataAlerts.searchInputPlaceholder"
    />
  );

  const dataAlertsTabs = [
    {
      trigger: {
        value: "all",
        label: "dashboards.dataAlerts.tabs.trigger.all",
        labelOptions: { count: records.length },
      },
      content: (
        <OverviewTabContent
          value="all"
          title="dashboards.dataAlerts.tabs.content.all.title"
          description="dashboards.dataAlerts.tabs.content.all.description"
          subDescription="dashboards.dataAlerts.tabs.content.all.subDescription"
          subDescriptionOptions={{ date: spanishDateTransformer(new Date()) }}
        >
          <DataAlertsDashboardTable data={records} />
        </OverviewTabContent>
      ),
    },
    {
      trigger: {
        value: "critical",
        label: "dashboards.dataAlerts.tabs.trigger.critical",
        labelOptions: { count: criticalRecords.length },
      },
      content: (
        <OverviewTabContent
          value="critical"
          title="dashboards.dataAlerts.tabs.content.critical.title"
          description="dashboards.dataAlerts.tabs.content.critical.description"
        >
          <DataAlertsDashboardTable data={criticalRecords} />
        </OverviewTabContent>
      ),
    },
    {
      trigger: {
        value: "error",
        label: "dashboards.dataAlerts.tabs.trigger.error",
        labelOptions: { count: errorRecords.length },
      },
      content: (
        <OverviewTabContent
          value="error"
          title="dashboards.dataAlerts.tabs.content.error.title"
          description="dashboards.dataAlerts.tabs.content.error.description"
        >
          <DataAlertsDashboardTable data={errorRecords} />
        </OverviewTabContent>
      ),
    },
    {
      trigger: {
        value: "warning",
        label: "dashboards.dataAlerts.tabs.trigger.warning",
        labelOptions: { count: warningRecords.length },
      },
      content: (
        <OverviewTabContent
          value="warning"
          title="dashboards.dataAlerts.tabs.content.warning.title"
          description="dashboards.dataAlerts.tabs.content.warning.description"
        >
          <DataAlertsDashboardTable data={warningRecords} />
        </OverviewTabContent>
      ),
    },
    {
      trigger: {
        value: "info",
        label: "dashboards.dataAlerts.tabs.trigger.info",
        labelOptions: { count: infoRecords.length },
      },
      content: (
        <OverviewTabContent
          value="info"
          title="dashboards.dataAlerts.tabs.content.info.title"
          description="dashboards.dataAlerts.tabs.content.info.description"
        >
          <DataAlertsDashboardTable data={infoRecords} />
        </OverviewTabContent>
      ),
    },
  ];

  const onTagFilterSelected = (tag, isChecked) => {
    if (isChecked) {
      setParams({ ...params, tags: [...params.tags, tag] });
    } else {
      const tags = [...params.tags];
      const index = tags.indexOf(tag);
      tags.splice(index, 1);
      setParams({ ...params, tags: tags });
    }
  };

  const onAllTagsSelected = (isChecked) => {
    if (isChecked) {
      setParams({ ...params, tags: tagSelectables.map((x) => x.label) });
    } else {
      setParams({ ...params, tags: [] });
    }
  };

  useEffect(() => {
    findTagSelectables();
    trackEvent({
      page: "data-alerts-dashboard",
      event: "page-load",
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    findDataAlertRecords();
  }, [params]);

  return (
    <>
      <div className="px-4 pb-4 sm:px-6">
        <BreadcrumbPanel items={breadcrumbItems} />
      </div>
      <div className="flex flex-row gap-x-4 p-4">
        <DataAlertsFilters
          selectables={tagSelectables}
          onTagFilterSelected={onTagFilterSelected}
          onAllTagsSelected={onAllTagsSelected}
        />
        <div className="w-full">
          <OverviewTabs
            options={SearchInputComponent}
            tabs={dataAlertsTabs}
            defaultTabValue="all"
          />
        </div>
      </div>
    </>
  );
};

export default DataAlertsDashboardContainer;
